<template>
    <q-card flat bordered class="obra-card">
        <q-card-section class="bg-blue-1">
            <div class="row items-center">
                <q-icon name="business" size="24px" color="primary" class="q-mr-sm" />
                <div class="text-subtitle1 text-weight-medium text-primary">
                    Seleccionar Obra
                </div>
            </div>
        </q-card-section>

        <q-card-section>
            <div class="row q-col-gutter-md items-end">
                <div class="col-12">
                    <GltnSelectObra v-model="obraBusqueda" @update:modelValue="obraUpdate" label="Buscar Obra o Cliente"
                        :cliente="clienteBusqueda?.A1_COD" hint="Seleccione una obra existente o cree una nueva">
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </GltnSelectObra>
                </div>

                <div class="col-12">
                    <div class="row q-gutter-sm">
                        <q-btn unelevated color="primary" icon="add" label="Nueva Obra" @click="obraOpen('add')"
                            class="col">
                            <q-tooltip>Crear una nueva obra</q-tooltip>
                        </q-btn>
                        <q-btn outline color="primary" icon="edit" label="Editar Obra" @click="obraOpen('edit')"
                            :disable="!obraBusqueda" class="col">
                            <q-tooltip>Editar la obra seleccionada</q-tooltip>
                        </q-btn>
                    </div>
                </div>
            </div>

            <!-- Obra seleccionada preview -->
            <q-card v-if="obraBusqueda" flat bordered class="obra-preview q-mt-md">
                <q-card-section class="bg-grey-1">
                    <div class="row items-center">
                        <q-icon name="check_circle" color="positive" size="20px" class="q-mr-sm" />
                        <div class="text-body2 text-weight-medium">Obra Seleccionada</div>
                    </div>
                </q-card-section>
                <q-card-section>
                    <div class="row items-center q-gutter-md">
                        <div class="col">
                            <div class="text-body1 text-weight-medium">{{ obraBusqueda.referencia }}</div>
                            <div class="text-caption text-grey-7" v-if="obraBusqueda.cliente">
                                <q-icon name="business" size="14px" />
                                {{ obraBusqueda.cliente.A1_NOME }}
                            </div>
                        </div>

                        <!-- Alerta de deuda -->
                        <div v-if="deudas && deudas.has_debt" class="col-auto">
                            <q-btn
                                flat
                                dense
                                round
                                color="negative"
                                icon="warning"
                                size="md"
                            >
                                <q-tooltip>Cliente tiene deuda. Haga click para más información</q-tooltip>
                                <q-menu anchor="bottom right" self="top right" >
                                    <q-card flat bordered style="width: 350px">
                                        <q-card-section class="bg-negative text-white">
                                            <div class="row items-center">
                                                <q-icon name="warning" size="24px" class="q-mr-sm" />
                                                <div class="text-subtitle2 text-weight-bold">
                                                    Cliente con Deuda
                                                </div>
                                            </div>
                                        </q-card-section>

                                        <q-card-section class="q-pa-md">
                                            <div class="text-subtitle2 text-weight-medium q-mb-md">
                                                Situación: {{ deudas.situacion && typeof deudas.situacion === 'object' ? Object.values(deudas.situacion).length : 0 }} entidades
                                            </div>
                                            <q-scroll-area style="height: 300px">
                                                <div v-for="(entidad, idx) in deudas.situacion" :key="idx" class="q-mb-md border-item">
                                                    <div class="text-body2 text-weight-bold q-mb-xs">
                                                        {{ entidad.entidad }}
                                                    </div>
                                                    <div class="text-caption q-ml-md">
                                                        <div class="row q-gutter-md">
                                                            <div class="col-6">
                                                                <span class="text-grey-7">Situación:</span>
                                                                <span class="text-weight-medium" :class="entidad.situacion > 1 ? 'text-negative' : 'text-warning'">
                                                                    {{ entidad.situacion }}
                                                                </span>
                                                            </div>
                                                            <div class="col-6">
                                                                <span class="text-grey-7">Monto:</span>
                                                                <span class="text-weight-medium">${{ entidad.monto.toLocaleString() }}</span>
                                                            </div>
                                                        </div>
                                                        <div class="row q-gutter-md q-mt-xs">
                                                            <div class="col-6">
                                                                <span class="text-grey-7">Días de atraso:</span>
                                                                <span class="text-weight-medium">{{ entidad.diasAtrasoPago }}</span>
                                                            </div>
                                                            <div class="col-6">
                                                                <span class="text-grey-7">Refinanciaciones:</span>
                                                                <q-icon
                                                                    :name="entidad.refinanciaciones ? 'check_circle' : 'cancel'"
                                                                    :color="entidad.refinanciaciones ? 'positive' : 'grey-5'"
                                                                    size="16px"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div v-if="entidad.procesoJud" class="q-mt-xs">
                                                            <q-icon name="error" color="negative" size="16px" class="q-mr-xs" />
                                                            <span class="text-weight-bold text-negative">Proceso Judicial</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </q-scroll-area>
                                        </q-card-section>
                                    </q-card>
                                </q-menu>
                            </q-btn>
                        </div>
                    </div>
                </q-card-section>
            </q-card>
        </q-card-section>
        <q-inner-loading :showing="loading" class="bg-white opacity-75">
            <q-spinner-hourglass size="50px" color="primary" />
            <p>Verificando deudas...</p>
        </q-inner-loading>
    </q-card>

    <ObraCrud v-model="obraCrudOpen" :oper="oper" :id="obraBusqueda?.id" @stored="onObraStored" />
</template>

<script setup>
import GltnSelectObra from '@/components/form/GltnSelectObra.vue';
import BcraService from '@/services/BcraService.js';
import ObraCrud from '@/components/obra/ObraCrud.vue';
import { ref, onMounted, computed } from 'vue';
import { useCartStore } from '@/stores/useCartStore';
import { isEmpty } from 'lodash'
const cartStore = useCartStore();

const clienteBusqueda = ref()
const obraBusqueda = ref(null)
const obraCrudOpen = ref(false)
const oper = ref('add')
const loading = ref(false)
const deudas = ref(null)

const obraOpen = (operType) => {
    oper.value = operType
    obraCrudOpen.value = true
}

const onObraStored = (obra) => {
    obraBusqueda.value = obra
    obraUpdate()
}

const obraUpdate = async () => {
    loading.value = true
    try {
        cartStore.setObra(obraBusqueda.value)
        const result = await BcraService.getDeudas(obraBusqueda.value?.cliente?.A1_CGC)
        deudas.value = result
    } catch (error) {
        console.error('Error al consultar deudas:', error)
        deudas.value = null
    } finally {
        loading.value = false
    }
}
const obra = computed(() => cartStore.getObra)
onMounted(() => {
    obraBusqueda.value = isEmpty(obra.value) ? null : obra.value

})


</script>

<style lang="scss" scoped>
.obra-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.obra-preview {
    border-radius: 8px;
    animation: fadeIn 0.4s ease-out;
    border-left: 4px solid var(--q-positive);
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
